import api from "./api";
import { getStoredClientId, getStoredUser } from "../utils/tokenUtils";

const reviewService = {
  /**
   * Submit test case for review
   */
  submitForReview: async (testCaseId, reviewerId) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      test_case_id: testCaseId,
      reviewer_id: reviewerId,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("api/reviews/submit", payload);
    return response.data;
  },

  /**
   * Get all reviews for a test case
   */
  getReviews: async (testCaseId) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      test_case_id: testCaseId,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("api/reviews/list", payload);
    return response.data;
  },

  /**
   * Get review by ID
   */
  getReviewById: async (reviewId) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      review_id: reviewId,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("api/reviews/get", payload);
    return response.data;
  },

  /**
   * Approve test case
   */
  approveTestCase: async (reviewId, comments = null) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      review_id: reviewId,
      comments,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("api/reviews/approve", payload);
    return response.data;
  },

  /**
   * Request changes for test case
   */
  requestChanges: async (reviewId, comments) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      review_id: reviewId,
      comments,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("api/reviews/request-changes", payload);
    return response.data;
  },

  /**
   * Reject test case
   */
  rejectTestCase: async (reviewId, comments) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      review_id: reviewId,
      comments,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("api/reviews/reject", payload);
    return response.data;
  },

  /**
   * Create new version of test case
   */
  createVersion: async (testCaseId) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      test_case_id: testCaseId,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("api/reviews/create-version", payload);
    return response.data;
  },

  /**
   * Get all versions of a test case.
   * Response `data` includes `versions` (raw rows) and `history` (UI timeline, newest first).
   */
  getVersions: async (testCaseId) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      test_case_id: testCaseId,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("api/reviews/versions", payload);
    return response.data;
  },

  /**
   * Test cases in a project (one row per chain: latest version). Prefer URL `client_id` when present so it matches the project in the query string (e.g. chief-admin / multi-client).
   * @param {string} [clientIdOverride] - from `searchParams.get('client_id')`
   */
  listProjectHistory: async (projectId, sectionId, clientIdOverride) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      project_id: projectId,
      client_id: clientIdOverride || storedClientId || user?.client_id,
      ...(sectionId ? { section_id: sectionId } : {}),
    };
    const response = await api.post("api/reviews/project-history", payload);
    return response.data;
  },

  /**
   * Get all test cases assigned to the logged-in reviewer
   */
  getMyTestCases: async (options = {}) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      page: options.page || 1,
      limit: options.limit || 10,
      review_status: options.review_status || null,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("api/reviews/my-test-cases", payload);
    return response.data;
  },
};

export default reviewService;
